import { GridCustomCellProps } from "@progress/kendo-react-grid";
import "./custom-cells.css";
import { useEffect, useState } from "react";
import { InputBase } from "@mui/material";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { useTableCellNavigation } from "./use-table-cell-navigation.hook";
import { useBulkChange } from "./use-bulk-change.hook";

export const UnitCustomCell = (props: GridCustomCellProps) => {
	const [input, setInput] = useState<string>("");

	// Fallback for rows created before the unit was snapshotted onto
	// job_items — resolved live from the article master data via code_e.
	const articleUnit = useCentralStore((state) => {
		const codeE = props.dataItem.code_e;
		if (!codeE) return "";
		const articlesForCode = state.articles[codeE];
		if (!articlesForCode) return "";
		return Object.values(articlesForCode)[0]?.unit ?? "";
	});

	const { handleChange: handleBulkChange } = useBulkChange({
		dataItem: props.dataItem,
		dataIndex: props.dataIndex,
		field: props.field,
		onChange: props.onChange,
	});

	const { handleKeyDown: handleNavigationKeyDown } = useTableCellNavigation({
		field: props.field,
		dataIndex: props.dataIndex,
		focusSelector: (targetField: string, targetRowIndex: number) =>
			`input[data-field="${targetField}"][data-row-index="${targetRowIndex}"]`,
	});

	useEffect(() => {
		if (props.field) {
			setInput(props.dataItem[props.field] ?? articleUnit ?? "");
		}
	}, [props.dataItem, props.field, articleUnit]);

	const applyChange = (event: React.SyntheticEvent<EventTarget, Event>) => {
		if (props.onChange && props.field) {
			const currentValue = props.dataItem[props.field];
			if (currentValue !== input) {
				props.onChange({
					dataItem: props.dataItem,
					field: props.field,
					syntheticEvent: event,
					value: input,
					dataIndex: props.dataIndex,
				});
			}
		}
	};

	const handleBlur = (
		event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		applyChange(event);
	};

	const handleKeyDown = (
		event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
	) => {
		if (event.key === "Enter") {
			applyChange(event);
			(event.target as HTMLInputElement).blur();
			return;
		}

		if (
			event.key === "Backspace" &&
			event.target instanceof HTMLInputElement &&
			event.target.value === ""
		) {
			event.preventDefault();
			handleBulkChange(
				"",
				event.metaKey || event.ctrlKey,
				event.shiftKey,
				event.altKey
			);
			return;
		}

		if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
			const input = event.target as HTMLInputElement;
			if (event.key === "ArrowRight") {
				const isAtEnd = input.selectionStart === input.value.length;
				if (!isAtEnd) return;
			} else {
				const isAtStart = input.selectionStart === 0;
				if (!isAtStart) return;
			}
		}

		handleNavigationKeyDown(event);
	};

	return (
		<td className="custom-td">
			<InputBase
				data-testid={`unit-custom-cell-input-${props.field}`}
				key={props.id}
				value={input}
				onChange={(event) => {
					setInput(event.target.value);
				}}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				fullWidth
				onFocus={(e) => e.target.select()}
				inputProps={{
					"data-field": props.field,
					"data-row-index": props.dataIndex,
				}}
				sx={{
					height: "22px",
					fontSize: "12px",
					paddingLeft: 1,
					"& input": {
						textOverflow: "ellipsis",
						padding: 0,
					},
					"&:hover": {
						backgroundColor: "rgba(0, 0, 0, 0.04)",
					},
					"&.Mui-focused": {
						backgroundColor: "rgba(0, 0, 0, 0.04)",
					},
					...props.style,
				}}
			/>
		</td>
	);
};
